import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Utensils, BookOpen, Users, Heart, Clock, MapPin, PawPrint, Truck, Leaf } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

// Featured programs always shown first
const featuredPrograms = [
  {
    id: 'featured-1',
    name: 'Living Fruit Outreach',
    description: 'Our 501(c)(3) outreach ministry serves families in need through food pantry services, community outreach events, and emergency assistance. Together we provide hope and tangible support to our neighbors.',
    schedule: 'Weekly Food Pantry & Monthly Events',
    icon: Leaf,
    color: 'bg-green-600',
    image: 'https://images.unsplash.com/photo-1593113646773-028c64a8f1b8?w=600',
    link: '/outreach',
    taxStatus: '501(c)(3)',
    featured: true
  },
  {
    id: 'featured-2',
    name: 'Smallest Souls Animal Rescue',
    description: 'Our 501(c)(3) animal rescue ministry is dedicated to saving the lives of animals in need. We rescue, rehabilitate, and rehome pets while promoting responsible pet ownership in our community.',
    schedule: 'Ongoing Rescue & Adoption Services',
    icon: PawPrint,
    color: 'bg-amber-600',
    image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600',
    link: '/animal-rescue',
    taxStatus: '501(c)(3)',
    featured: true
  },
];

const defaultPrograms = [
  {
    id: '1',
    name: 'Food Pantry',
    description: 'Providing nutritious food to families in need throughout Kissimmee. Our pantry serves hundreds of families monthly with fresh produce, canned goods, and essential items.',
    schedule: 'Saturdays 9:00 AM - 12:00 PM',
    icon: Utensils,
    color: 'bg-gold',
    image: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?w=600',
    link: '/pantry-registration'
  },
  {
    id: '2',
    name: 'Sunday Worship',
    description: 'Join us for uplifting worship services featuring powerful messages, praise, and fellowship. Everyone is welcome to experience God\'s love.',
    schedule: 'Sundays 10:00 AM',
    icon: BookOpen,
    color: 'bg-burgundy',
    image: 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=600'
  },
  {
    id: '3',
    name: 'Community Outreach Events',
    description: 'Serving our neighbors through various charitable initiatives including clothing drives, disaster relief, home repairs, and special holiday programs.',
    schedule: 'Monthly Events',
    icon: Truck,
    color: 'bg-burgundy',
    image: 'https://images.unsplash.com/photo-1728034824552-54121ea0f02e?w=600',
    link: '/outreach'
  },
  {
    id: '4',
    name: 'Youth Ministry',
    description: 'Engaging programs for young people to grow in faith, build friendships, and discover their purpose through fun activities and mentorship.',
    schedule: 'Fridays 6:00 PM',
    icon: Users,
    color: 'bg-olive',
    image: 'https://images.unsplash.com/photo-1725596854669-61fb4badaf4d?w=600'
  },
];

const pantryLocations = [
  {
    id: '1',
    name: 'Main Campus Pantry',
    address: '109 N Beaumont Avenue #1078, Kissimmee, FL 34741',
    schedule: 'Saturdays 9:00 AM - 12:00 PM',
    phone: '(407) 216-2853'
  },
];

export default function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [pantries, setPantries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programsRes, pantriesRes] = await Promise.all([
          axios.get(`${API}/programs`),
          axios.get(`${API}/pantries`)
        ]);
        setPrograms(programsRes.data);
        setPantries(pantriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const displayPrograms = programs.length > 0 ? programs : defaultPrograms;
  const displayPantries = pantries.length > 0 ? pantries : pantryLocations;
  
  // Always show featured programs first, then other programs
  const allPrograms = [...featuredPrograms, ...displayPrograms];

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="programs-page">
      {/* Ministry Banner */}
      <div className="w-full">
        <img 
          src={MINISTRY_BANNER}
          alt="Living Fruit Ministry"
          className="w-full h-auto object-cover"
          style={{ maxHeight: '300px', objectPosition: 'center' }}
        />
      </div>

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">Get Involved</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Our Programs</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Discover the many ways you can grow in faith and serve others through our ministry programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-12" data-testid="programs-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allPrograms.map((program, i) => {
              const IconComponent = program.icon || Heart;
              const CardWrapper = program.link ? 'a' : 'div';
              const cardProps = program.link ? { href: program.link } : {};
              
              return (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl overflow-hidden border border-gold/10 hover:border-gold/30 transition-all duration-500 hover:-translate-y-2 group"
                  data-testid={`program-${i}`}
                >
                  <CardWrapper {...cardProps} className={program.link ? 'block' : ''}>
                    {/* Image */}
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={program.image || 'https://images.unsplash.com/photo-1655392032737-6a4fabe2f15b?w=600'}
                        alt={program.name}
                        className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className={`absolute top-4 left-4 ${program.color || 'bg-burgundy'} p-3 rounded-xl`}>
                        <IconComponent size={24} className="text-white" />
                      </div>
                      {program.taxStatus && (
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                          <span className="text-xs font-semibold text-green-700">{program.taxStatus}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <h3 className="font-serif text-xl text-charcoal mb-2">{program.name}</h3>
                      <p className="text-charcoal/60 text-sm mb-4 line-clamp-3">{program.description}</p>
                      {program.schedule && (
                        <div className="flex items-center gap-2 text-sm text-gold">
                          <Clock size={16} />
                          <span>{program.schedule}</span>
                        </div>
                      )}
                      {program.link && (
                        <div className="mt-4 text-burgundy font-medium text-sm group-hover:underline">
                          Learn More →
                        </div>
                      )}
                    </div>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Food Pantry Locations */}
      <section className="py-16 bg-white" data-testid="pantry-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">Food Assistance</span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-4">Pantry Locations</h2>
            <p className="text-charcoal/70 max-w-xl mx-auto">
              Find a food pantry near you. All are welcome, no questions asked.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {displayPantries.map((pantry, i) => (
              <motion.div
                key={pantry.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-cream rounded-2xl p-6 border border-gold/20"
                data-testid={`pantry-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="bg-gold p-3 rounded-xl">
                    <Utensils size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-charcoal mb-2">{pantry.name}</h3>
                    <div className="space-y-2 text-sm text-charcoal/70">
                      <p className="flex items-start gap-2">
                        <MapPin size={16} className="text-gold mt-0.5 flex-shrink-0" />
                        {pantry.address}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock size={16} className="text-gold flex-shrink-0" />
                        {pantry.schedule}
                      </p>
                      {pantry.phone && (
                        <p className="text-burgundy font-medium">{pantry.phone}</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-burgundy" data-testid="programs-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Ready to Get Involved?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Whether you want to volunteer, participate, or learn more about our programs, we'd love to hear from you.
            </p>
            <a
              href="/contact"
              className="inline-block bg-gold text-charcoal px-8 py-4 rounded-full font-sans uppercase tracking-widest text-xs hover:bg-white transition-colors"
              data-testid="programs-contact-btn"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
